import { simpleApi } from "~/common/API/simpleApi";
import { ElNotification } from "element-plus";
import type {
  IModerationUpdateRequest,
  PublisherResponse,
} from "~/interfaces/publisher/moderation";
import type { IYuriSaveData } from "~/interfaces/publisher/payments";
import type { BindMessenger } from "~/services/AuthService";

export default class PublisherService {
  static getPublisherPlaces = (
    userId: number,
    moderate = false,
    pageNumber?: number,
    perPage?: number,
    platform?: string,
    search?: string
  ) => {
    if (!useCookie("authorizedP").value) return;
    
    // Определяем endpoint в зависимости от платформы
    let endpoint = "/place/telegram/search";
    if (platform === "all") {
      endpoint = "/place/search";
    } else if (platform === "max") {
      endpoint = "/place/max/search";
    } else if (platform === "vk" || platform === "vkontakte") {
      endpoint = "/place/vk/search";
    }
    
    if (moderate)
      endpoint = "/publisher/moderation/search?status[]=rejected&status[]=pending&status[]=rework";

    const params: any = {
      userId,
      perPage: perPage || (moderate ? 1000 : 50), // Для модерации всегда загружаем все, для активных - по умолчанию 50
    };

    // Добавляем pageNumber только если он передан и это не модерация
    if (pageNumber && !moderate) {
      params.pageNumber = pageNumber;
    }

    // Поиск по названию канала или ссылке на него — фильтрует бэк.
    const searchQuery = search?.trim();
    if (searchQuery) {
      params.search = searchQuery;
    }

    return new Promise((resolve) => {
      simpleApi(endpoint, { params })
        .then((res: any) => {
          // Для модерации возвращаем только records, для активных - полный ответ с мета-информацией
          if (moderate) {
            resolve(res.records);
          } else {
            resolve(res); // Возвращаем полный ответ с meta для пагинации
          }
        })
        .catch((err) => {
          console.error("getPublisherPlaces", err);
          resolve(false);
          ElNotification({
            title: "Ошибка получения данных",
            message: "попробуйте чуть позже или свяжитесь с администратором...",
            type: "error",
          });
        });
    });
  };

  static getPublisherActivePlaces = async (userId: number, placeId: number, page: number = 1, pageSize: number = 20) => {
    const endpoint = "/place/search";
    const response: any = await simpleApi(endpoint, {
      params: {
        userId,
        isActive: true,
        pageNumber: page,
        perPage: pageSize,
        "sortFields[]": "id",
        "sortDirections[]": "desc",
      },
    });
    const records = response.records.filter(
      (item: any) => !item.haveModerationRequests && item.id !== placeId
    );
    return { records, meta: response.meta };
  };

  static getPublisherOrders = (params: any) => {
    const url = `/advert/publications`;
    return new Promise((resolve) => {
      simpleApi(url, {
        params,
      })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static getPublicationCount = (userId: number) => {
    const url = `/publisher/publication/count`;
    return new Promise((resolve) => {
      simpleApi(url, {
        params: {
          id: userId,
          perPage: 100,
        },
      })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve([]);
        });
    });
  };

  static setPublisherEmail = (email: string, id: number) => {
    const url = `/user/set-email-request`;
    return new Promise((resolve) => {
      simpleApi(url, {
        method: "POST",
        body: { email, id },
      })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve(false);
        });
    });
  };

  static publisherEmailConfirmation = async (code: string): Promise<PublisherResponse | null> => {
    const url = `/user/email-confirm`;
    try {
      const res: any = await simpleApi(url, {
        method: "POST",
        body: { code },
      });
      if (!res) return null;
      return res;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  static getPlaceModeration = (userId: number) => {
    const url = `/moderation/search`;
    return new Promise((resolve) => {
      simpleApi(url, {
        params: {
          "status[]": "rework",
          userId,
        },
      })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error(err);
          resolve(false);
        });
    });
  };

  static updateModerationRequest = (data: IModerationUpdateRequest) => {
    const url = `/publisher/edit-place-moderation-request`;
    return new Promise((resolve) => {
      simpleApi(url, {
        method: "PUT",
        body: data,
      }).then((res) => {
        resolve(res);
      });
    });
  };

  static updateUser = async (data: {
    id: number;
    name?: string;
    email?: string;
    telegram?: string;
    telegramId?: number;
    // Битовые маски настроек уведомлений: отдельной ручки под них нет
    notificationChannel?: number;
    notificationGroups?: number;
  }) => {
    const response = await simpleApi("/user/update", {
      method: "PUT",
      body: data,
      params: { id: data.id },
    });
    return response;
  };

  static getUser = async () => {
    const endpoint = "/user";
    const response: any = await simpleApi(endpoint, { method: "GET" });
    return response;
  }

  static orderReject = async (payload: { id: number; reason: string }) => {
    const endpoint = "/advert/publication/publisher-reject";
    try {
      const response = await simpleApi(endpoint, { method: "PUT", body: payload });
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  static orderCancel = async (payload: { id: number; reason: string }) => {
    const endpoint = "/advert/publication/publisher-cancel";
    try {
      const response = await simpleApi(endpoint, { method: "PUT", body: payload });
      return response;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  static orderApprove = async (payload: {
    publicationDateTime: string;
    comment?: string;
    id: number;
  }) => {
    const endpoint = "/advert/publication/publisher-approve";
    // Ошибку не глушим: вызывающий код должен показать её пользователю
    // (например, code 1595 — публикацию уже взяли в работу).
    const response = await simpleApi(endpoint, { method: "PUT", body: payload });
    return response ?? null;
  };

  static getPlaceOrder = async (payload: { placeId: number }) => {
    const endpoint = "/advert/publications";
    try {
      const response = await simpleApi(endpoint, { params: { ...payload, perPage: 100 } });
      return response;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  static sendLink = async (payload: { url: string; id: number }) => {
    const endpoint = "/advert/publication/set-link";
    try {
      return await simpleApi(endpoint, {
        method: "PUT",
        body: { ...payload },
      });
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  static addPublisherPaymentsData = async (payload: any) => {
    // let endpoint = "/user/payments/details/create/legal";
    // if (payload.type === '1' || payload.type === '2') {
    //   endpoint = "/user/payments/details/create/private";
    // }
    const endpoint =
      payload.type === "1" //|| payload.type === "2"
        ? "/user/payments/details/individual/save"
        : "/user/payments/details/legal/save"
    delete payload.type;

    try {
      return await simpleApi(endpoint, {
        method: "POST",
        body: payload,
      });
    } catch (err) {
      console.error("addPublisherPaymentsData: Error:", err);
      return null;
    }
  };

  static addPublisherPaymentsDataYuri = async (payload: IYuriSaveData) => {
    const endpoint = "/user/payments/details/legal/save";
    return await simpleApi(endpoint, {
      method: "POST",
      body: payload,
    });
  };

  static deletePublisherPaymentsData = async (id: number) => {
    const endpoint = "/user/payments/details";
    return await simpleApi(endpoint, {
      method: "DELETE",
      params: { id },
    });
  };

  // Только данные кошелька. Карты сюда не подмешиваем — за ними есть общий
  // getCombinedCards() (Т-банк + Точка одним запросом).
  static getPublisherPaymentsData = async () => {
    const endpoint = "/user/wallet/publisher";
    let result: any = null;

    try {
      const response: any = await simpleApi(endpoint);
      if (response?.error && response?.error?.code === 1519) {
        return null;
      }
      // console.log("Responce", response);

      if (!response || response === "") {
        result = {
          details: null,
          type: 0,
          balance: 0,
          withdrawalAvailable: 0,
          hold: 0,
          estimatedProfit: 0,
          list: [],
        };
      } else if (!response?.error) {
        result = { ...response };
        if (!result?.details) {
          result.details = null;
          result.type = 0;
        }
      }
      return result;
    } catch (err) {
      console.error(123, err);
    } finally {
      return result;
    }
  };

  static updatePublisherPaymentsData = async (payload: { id: number; phoneNumber: string }) => {
    const endpoint = "/user/payments/details/update";
    try {
      const response: any = await simpleApi(endpoint, { method: "PUT", body: payload });
      return response;
    } catch (err) {
      console.error("updatePublisherPaymentsData: Error:", err);
      return null;
    }
  };

  static getPlaceOrderCount = async (placeId: number) => {
    const endpoint = "/advert/orders/count";
    try {
      const response: any = await simpleApi(endpoint, { params: { publisherId: placeId } });
      return response;
    } catch (err) {
      console.error(err);
      return null;
    }
  };



  static paymentAction = async () => {
    const endpoint = "/finance/acquiring/t-bank/add-card";
    try {
      const response: any = await simpleApi(endpoint, { method: "POST" });
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  static addNominalAccountCard = async (cardNumber: string) => {
    const endpoint = "/finance/acquiring/nominal-account/add-card";
    try {
      const response: any = await simpleApi(endpoint, {
        method: "POST",
        body: { cardNumber },
      });
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  // Единый список карт: id — карта в Т-банке, nominalAccountCardId — карта в Точке.
  static getCombinedCards = async () => {
    const endpoint = "/finance/acquiring/combined/get-card-list";
    try {
      const response: any = await simpleApi(endpoint, { method: "GET" });
      return response;
    } catch (err) {
      console.error("getCombinedCards", err);
      return null;
    }
  };

  static getPlaceStatistic = async (placeId: number) => {
    const endpoint = "/place/telegram/daily-stats";
    try {
      const response: any = await simpleApi(endpoint, { params: { id: placeId } });
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  static actionPayment = async () => {
    const endpoint = "/finance/acquiring/withdrawal";
    try {
      const response: any = await simpleApi(endpoint, { method: "POST" });
      console.log(response);
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  static getDialogCount = async () => {
    const endpoint = "/api/v1/ticket/unread/count";
    const response: any = await simpleApi(endpoint);
    return response;
  };

  static getSummary = async () => {
    const response: any = await simpleApi("/publisher/summary");
    return response;
  };

  // Общий эндпоинт для выплаты по ном. счетам и мультирасчётам.
  // cardId — id карты в Т-банке, nominalAccountCardId — id карты в Точке.
  // Передаём оба идентификатора выбранной карты (пустые не отправляем).
  static getNominalAccountWithdrawal = async (params?: {
    cardId?: string | number;
    nominalAccountCardId?: string | number;
  }) => {
    const endpoint = "/finance/acquiring/combined-withdrawal";
    try {
      const body = {
        ...(params?.cardId ? { cardId: params.cardId } : {}),
        ...(params?.nominalAccountCardId
          ? { nominalAccountCardId: params.nominalAccountCardId }
          : {}),
      };
      const response: any = await simpleApi(endpoint, {
        method: "POST",
        body: Object.keys(body).length ? body : undefined,
      });
      return response;
    } catch (err) {
      console.error("getNominalAccountWithdrawal", err);
      return null;
    }
  };

  static getNominalAccountDealHistory = async (params?: {
    pageNumber?: number;
    perPage?: number;
    sortFields?: string[];
    sortDirections?: string[];
  }) => {
    const endpoint = "/finance/acquiring/nominal-account/deal-history";
    try {
      const response: any = await simpleApi(endpoint, {
        method: "GET",
        params: {
          ...(params?.pageNumber ? { pageNumber: params.pageNumber } : {}),
          ...(params?.perPage ? { perPage: params.perPage } : {}),
          ...(params?.sortFields ? { "sortFields[]": params.sortFields } : {}),
          ...(params?.sortDirections ? { "sortDirections[]": params.sortDirections } : {}),
        },
      });
      return response;
    } catch (err) {
      console.error("getNominalAccountDealHistory", err);
      return null;
    }
  };

  static recoveryPassword = async (email: string) => {
    const endpoint = "/user/password/request";
    return await simpleApi(endpoint, { method: "POST", body: { email, role: 3 } });
  };


static resetPasswordWoAuthConfirm = async (code: string) => {
    const endpoint = "/user/password/confirm";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { code } });
    return response;
};
  
  static resetPasswordConfirm = async (email: string, code: string) => {
    const endpoint = "/user/reset-password";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { email, code } });
    return response;
  };

  static simpleREsetPassword = async (email: string) => {
    const endpoint = "/user/password/reset";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { email } });
    return response;
  }

  static getPaymentsHistory = async (id: number, page: number = 1) => {
    const endpoint = "/finance/acquiring/transaction";
    const response: any = await simpleApi(endpoint, {
      params: {
        userId: id,
        pageNumber: page,
        "sortFields[]": "id",
        "sortDirections[]": "desc",
      },
    });
    return response;
  };

  static getWalletOperations = async (id: number, page: number = 1) => {
    const endpoint = "/finance/wallet/operation";
    const response: any = await simpleApi(endpoint, {
      params: {
        userId: id,
        userRole: "publisher",
        // Одна строка на публикацию — её последняя операция; операции без публикации приходят все
        latestOnly: 1,
        pageNumber: page,
        "sortFields[]": "id",
        "sortDirections[]": "desc",
      },
    });
    return response;
  };

  // static setNewPassword = async (email: string, code: string, password: string) => {
  //   const endpoint = "/user/set-password";
  //   const response: any = await simpleApi(endpoint, {
  //     method: "POST",
  //     body: { email, code, password },
  //   });
  //   return response;
  // };

  static setPostToOwnTelegram = async (payload: { id: number }) => {
    const endpoint = "/advert/publication/send-to-bot";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { ...payload } });
    if (response.error) {
      ElNotification({
        title: "Ошибка",
        message: response.error.message,
        type: "error",
      });
      return false;
    } else {
      ElNotification({
        title: "Успешно",
        message: "Пост успешно отправлен вам в Telegram",
        type: "success",
      });
    }
    return response;
  };

  // Автопостинг
  static checkAdmin = async (groupUrl: string) => {
    const endpoint = "/publisher/check-admin";
    const response: any = await simpleApi(endpoint, {
      method: "POST",
      body: { groupUrl },
    });
    return response;
  };

  static enableAutopost = async (publicationId: number) => {
    const endpoint = "/publisher/autopost";
    const response: any = await simpleApi(endpoint, {
      method: "POST",
      body: { publicationId },
    });
    return response;
  };

  static cancelAutopost = async (publicationId: number) => {
    const endpoint = "/publisher/autopost/cancel";
    const response: any = await simpleApi(endpoint, {
      method: "POST",
      body: { publicationId },
    });
    return response;
  };

  static getEmailRegData = async (email: string) => {
    const endpoint = "/user/registration/email/start";
    const response: any = await simpleApi(endpoint, {
      method: "POST",
      body: { identifier: email, role: 3 },
    });
    return response;
  };

  static registrationEmail = async (id: number, password: string) => {
    const endpoint = "/user/registration/email/step2";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { id, password } });
    return response;
  };

  static sendOrdAsk = async (id: number) => {
    const endpoint = "/advert/publication/ord-token";
    const response: any = await simpleApi(endpoint, { method: "PUT", body: { id } });
    return response;
  };

  // Ключ авторизации в боте. Ручки у мессенджеров раздельные: общей
  // /user/messenger/bot/generate-auth-key у бэка нет.
  static requestAuthKey = async (messenger: BindMessenger = "telegram") => {
    const endpoint = `/user/${messenger}/bot/generate-auth-key`;
    const response: any = await simpleApi(endpoint, { method: "POST" });
    return response;
  };

  static getUserFavorites = async (userId: number) => {
    const endpoint = "/user/favorite";
    const response: any = await simpleApi(endpoint, {
      method: "GET",
      params: { userId },
    });
    return response;
  };

  static getPlaceById = async (id: number, social?: string) => {
    let endpoint = "/place/telegram/get";
    if (social === "vk" || social === "vkontakte") {
      endpoint = "/place/vk/get";
    } else if (social === "max") {
      endpoint = "/place/max/get";
    }
    try {
      const response: any = await simpleApi(endpoint, { params: { id } });
      return response;
    } catch (err) {
      console.error("getPlaceById", err);
      return null;
    }
  };

  static setPlaceActive = async (payload: { id: number; isActive: boolean; social?: string; slug?: string }) => {
    const response: any = await simpleApi("/place/active", {
      method: "PUT",
      body: {
        isActive: payload.isActive,
        byPlace: {
          social: payload.social || "telegram",
          id: payload.id,
        },
        bySlug: payload.slug,
      },
    });
    return response;
  };

  static checkEmail = async (email: string) => {
    const endpoint = "/user/email/check";
    const response: any = await simpleApi(endpoint, { method: "GET", params: { email } });
    return response;
  };

  static sendToArchive = async (id: number) => {
    const endpoint = "/advert/publication/archive";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { id } });
    return response;
  };

  static sendToUnarchive = async (id: number) => {
    const endpoint = "/advert/publication/unarchive";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { id } });
    return response;
  };

  static clearPublisherPaymentsData = async (id: number) => {
    const endpoint = "/user/wallet/details/clear";
    const response: any = await simpleApi(endpoint, { method: "PUT", body: { id } });
    return response;
  };

  static requestConfirmCode = async (id: number, email: string, password: string) => {
    const endpoint = "/user/set-email-request";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { id, email, password } });
    return response;
  };

  static sendConfirmCode = async (code: number) => {
    const endpoint = "/user/email-confirm";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { code } });
    return response;
  };

  static sendChangeConfirmCode = async (code: number) => {
    const endpoint = "/user/change-email-confirm";
    const response: any = await simpleApi(endpoint, { method: "POST", body: { code } });
    return response;
  };

  // Пользователя бэк берёт из токена, id в теле не нужен
  static requestChengeConfirmCode = async (email: string) => {
    const endpoint = "/user/change-email-request"
    const response: any = await simpleApi(endpoint, { method: "POST", body: { email } });
    return response;
  }

  static setNewPassword = async (data: { email: string, code: string, password: string }) => {
    const endpoint = "/user/set-password";
    try {
      const response: any = await simpleApi(endpoint, { method: "POST", body: data });
      ElNotification({
        title: "Успешное сохранение пароля",
        message: "Спасибо! Вы можете уже использовать новый пароль для входа",
        type: "success",
      });
      return response;

    } catch (err) {
      console.error(err);
      ElNotification({
        title: "Ошибка сохранения пароля",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "error",
      });
      return null;
    }
  };

  static getUserPhone = async () => {
    const endpoint = "/user/wallet/publisher";
    const response: any = await simpleApi(endpoint, { method: "GET" });
    return response?.details?.phoneNumber || "";
  };

  static updateUserPassword = async (data: { oldPassword: string, password: string, id: number }) => {
    const endpoint = "/user/change-password";
    try {
      const response: any = await simpleApi(endpoint, { method: "POST", body: data });
      ElNotification({
        title: "Пароль успешно изменен",
        message: "Спасибо! Вы можете уже использовать новый пароль для входа",
        type: "success",
      });
      return response;
    } catch (err) {
      console.error(err);
      ElNotification({
        title: "Ошибка обновления пароля",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "error",
      });
      return null;
    }
  };

  static uploadFile = async (file: File): Promise<string | null> => {
    const endpoint = "/advert/creative/content-media-item/upload";
    const endpint2 = "/advert/creative/content-media-item/upload-check-status";

    const data = new FormData();
    data.append("file", file);
    try {
      const response: any = await simpleApi(endpoint, { method: "POST", body: data });
      if (!response.uploadId) {
        ElNotification({
          title: "Ошибка загрузки файла",
          message: "попробуйте чуть позже или свяжитесь с администратором...",
        });
        return null;
      }

      // Используем setInterval для проверки статуса каждую секунду, максимум 10 попыток
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 10;
        const intervalDelay = 1000; // 1 секунда

        const checkStatus = async () => {
          attempts++;

          try {
            const status: any = await simpleApi(endpint2, { params: { "ids[]": response.uploadId } });

            if (status.statuses?.[response.uploadId]?.status === "success") {
              clearInterval(intervalId);
              resolve(status.statuses[response.uploadId].cloudUrl);
              return;
            }
          } catch (error) {
            console.error(`Ошибка проверки статуса (попытка ${attempts}):`, error);
          }

          // Если достигли максимума попыток
          if (attempts >= maxAttempts) {
            clearInterval(intervalId);
            ElNotification({
              title: "Ошибка загрузки файла",
              message: "Файл не был загружен, попробуйте еще раз...",
              type: "error",
            });
            resolve(null);
          }
        };


        checkStatus();
        const intervalId = setInterval(checkStatus, intervalDelay);
      });
    } catch (err) {
      ElNotification({
        title: "Ошибка загрузки файла",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "error",
      });
      return null;
    }
  };

  static checkShowCaseIsExist = async (slug: string) => {
    const endpoint = "/user/showcase";
    try {
      const res = await simpleApi(endpoint, { method: "GET", params: { slug } });
      if (res) return true;
      return false;
    } catch (err) {
      return false;
    }
  }
}
